<script setup>
import { ref, computed, watch } from "vue";
import { PhMagnifyingGlass } from "@phosphor-icons/vue";

// Props
const props = defineProps({
  modelValue: [String, Number],
  options: {
    type: Array,
    required: true,
  },
  placeholder: String,
  label: String,
  disabled: {
    type: Boolean,
    default: false
  },
  filter: Function,
  error: {
    type: [String, Array],
    default: null
  }
});

// Emits
const emits = defineEmits(["update:modelValue"]);

// Local state
const inputRef = ref(null)
const search = ref("");
const open = ref(false);
const wrapperRef = ref(null);
const highlightedIndex = ref(-1)

// =========================
// ERROR STATE MANAGEMENT
// =========================
const serverError = ref(props.error)
const erroredValue = ref(props.modelValue)
const lastErrorMessage = ref(null)

defineExpose({
  focus: () => inputRef.value?.focus()
})

// =======================================================
// 1. NORMALIZE OPTIONS FOR FAST FILTERING
// =======================================================
const normalizedOptions = computed(() =>
  props.options.map((opt) => ({
    ...opt,
    _label: String(opt.label).toLowerCase(),
    _cats: (opt.categories || []).map((c) => String(c).toLowerCase()),
  }))
);

// =======================================================
// 2. SYNC modelValue → input field
// =======================================================
watch(() => props.modelValue, (newVal) => {
  const selected = props.options.find(
    (opt) => String(opt.value) === String(newVal)
  );

  if (selected) {
    search.value = selected.label;
  } else if (typeof newVal === "string" && newVal.trim() !== "") {
    search.value = newVal;
  } else {
    search.value = "";
  }
}, { immediate: true })

// =======================================================
// 3. WATCH ERROR PROP
// =======================================================
watch(() => props.error, (newError, oldError) => {
  // Get normalized error messages for comparison
  const normalizeError = (err) => {
    if (!err) return null
    return Array.isArray(err) ? err[0] : err
  }
  
  const newErrorMsg = normalizeError(newError)
  const oldErrorMsg = normalizeError(oldError)
  
  if (newErrorMsg) {
    // Check if this is a DIFFERENT error message than before
    const isErrorChanged = newErrorMsg !== oldErrorMsg
    
    // Always show error if:
    // 1. Error message changed (different validation issue)
    // 2. This is the first error
    if (isErrorChanged || !lastErrorMessage.value) {
      serverError.value = newError
      erroredValue.value = props.modelValue
      lastErrorMessage.value = newErrorMsg
    }
  } else {
    // No error from server - clear everything
    serverError.value = null
    lastErrorMessage.value = null
  }
}, { immediate: true })

// =======================================================
// 4. FILTERED OPTIONS
// =======================================================
const filteredOptions = computed(() => {
  const query = (search.value || "").toLowerCase();
  if (props.filter) return props.filter(search.value);
  if (!query) return normalizedOptions.value;

  return normalizedOptions.value.filter(
    (opt) =>
      opt._label.includes(query) ||
      opt._cats.some((c) => c.includes(query))
  );
});

// =======================================================
// 5. DEBOUNCED INPUT
// =======================================================
let debounceTimer;
const handleInput = (e) => {
  const value = e.target.value;
  clearTimeout(debounceTimer);
  debounceTimer = setTimeout(() => {
    search.value = value;
    open.value = true;
    highlightedIndex.value = -1;
  }, 120);
};

// =======================================================
// 6. SELECT OPTION
// =======================================================
const selectOption = (opt) => {
  search.value = opt.label;
  emits("update:modelValue", opt.value);
  open.value = false;
  highlightedIndex.value = -1;
  
  // Clear error if value changes from the errored value
  // Restore error if user returns to the exact errored value
  if (String(opt.value) === String(erroredValue.value)) {
    serverError.value = lastErrorMessage.value
  } else {
    serverError.value = null
  }
};

// =======================================================
// 7. OPEN/CLOSE HANDLERS
// =======================================================
const handleFocus = () => open.value = true;

const handleBlur = (event) => {
  if (!event.relatedTarget || !wrapperRef.value?.contains(event.relatedTarget)) {
    open.value = false;
    highlightedIndex.value = -1;
  }
};

// =======================================================
// 8. KEYBOARD NAVIGATION
// =======================================================
const handleKeyDown = (e) => {
  if (!open.value) return;

  const list = filteredOptions.value;
  const len = list.length;
  if (len === 0) return; // SAFEGUARD

  if (e.key === "ArrowDown") {
    e.preventDefault();
    highlightedIndex.value = (highlightedIndex.value + 1) % len;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    highlightedIndex.value = (highlightedIndex.value - 1 + len) % len;
  } else if (e.key === "Enter" && highlightedIndex.value >= 0) {
    e.preventDefault();
    selectOption(list[highlightedIndex.value]);
  } else if (e.key === "Escape") {
    open.value = false;
    highlightedIndex.value = -1;
  }
};

// =======================================================
// 9. TOGGLE DROPDOWN
// =======================================================
const toggleDropdown = () => {
  open.value = !open.value;
};

// =======================================================
// 10. COMPUTED ERROR DISPLAY
// =======================================================
const displayError = computed(() => {
  if (!serverError.value) return null
  return Array.isArray(serverError.value) ? serverError.value[0] : serverError.value
})

const inputClasses = computed(() => [
  'w-full rounded-md border bg-white pl-9 pr-8 py-2 text-sm text-gray-900 placeholder:text-gray-400 transition-all duration-200',
  displayError.value
    ? 'border-red-500 focus:outline-none focus:ring-1 focus:ring-red-300'
    : 'border-gray-300 hover:outline hover:outline-primary-400 focus:outline-none focus:ring-1'
])
</script>

<template>
  <div
    v-bind="$attrs"
    class="relative w-full"
    @focusout="handleBlur"
    tabindex="-1"
    ref="wrapperRef"
  >
    <!-- Label -->
    <label v-if="label" class="block mb-1 text-sm font-semibold text-gray-600">
      {{ label }}:
    </label>

    <!-- Input & Arrow -->
    <div class="relative">
      <PhMagnifyingGlass class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" :size="16" />
      <input
        ref="inputRef"
        :value="search"
        type="text"
        :placeholder="placeholder"
        :disabled="disabled"
        :class="inputClasses"
        @input="handleInput"
        @focus="handleFocus"
        @keydown="handleKeyDown"
      />

      <!-- Arrow -->
      <svg
        @click.stop="toggleDropdown"
        class="absolute right-2 top-3 h-4 w-4 text-gray-500 transition-transform duration-200 cursor-pointer"
        :class="{ 'rotate-180': open }"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>

      <!-- Dropdown -->
      <ul
        v-if="open && filteredOptions.length"
        class="absolute z-[999] mt-1 max-h-36 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg"
      >
        <li
          v-for="(opt, index) in filteredOptions"
          :key="opt.value"
          @mousedown.prevent
          @click="selectOption(opt)"
          :class="[
            'cursor-pointer px-3 py-1 text-sm hover:bg-accent hover:text-accent-foreground',
            highlightedIndex === index ? 'bg-primary-600 text-white' : ''
          ]"
        >
          {{ opt.label }}
        </li>
      </ul>

      <!-- No results -->
      <div
        v-if="open && !filteredOptions.length"
        class="absolute z-50 mt-1 w-full rounded-md border bg-white p-2 text-sm text-muted-foreground"
      >
        No results found
      </div>
    </div>

    <!-- Error -->
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <span v-if="displayError" class="text-red-600 text-sm mt-1 block">
        {{ displayError }}
      </span>
    </transition>
  </div>
</template>